/*
 * Past-event partition boundary — Orders rework (Duncan commission
 * BOARDROOM-2026-09-01-175306 items 1–4; grace proposal MY-2026-09-01-180752).
 *
 * THE safety rule (Duncan's rationale): a finished event must never present
 * live action affordances — someone completing get-ready for an ended event
 * and turning up at the next one is a nightmare. An order/event is PAST once
 * its server-carried `ends_at` instant plus the settle grace is behind now;
 * everything else — including a MISSING or unparseable date (older my-api,
 * degraded read, kill-switch) — is CURRENT, so a lost signal can never wrongly
 * bury a live order (the my-api degrade contract: fields absent ⇒ Current).
 *
 * ONE boundary for every consumer: the Orders Current/Past split, the Tickets
 * badge + wallet exclusion and the Get-ready home exclusion all call
 * isPastEvent — the counts and the screens can never disagree on where "past"
 * begins. Dates are SERVER-carried (events.starts_at/ends_at ride the my-api
 * wires); only the grace comparison lives client-side, per the commissioned
 * design.
 */
import type { Order } from './types'

/** Settle grace after `ends_at` before an event is treated as PAST — 72h, per
 *  the ratified proposal MY-2026-09-01-180752 (results settle, late transfers
 *  and disputes resolve, before the order sinks into "Past events"). ONE
 *  exported configurable constant — change it here and every consumer (orders
 *  partition, tickets badge/wallet, get-ready home) moves together. */
export const PAST_SETTLE_GRACE_MS = 72 * 60 * 60 * 1000

/** PAST ⇔ a real, parseable `endsAt` instant + the settle grace is strictly
 *  before `now`. Missing/unparseable ⇒ CURRENT (never wrongly buried). */
export function isPastEvent(endsAt: string | null | undefined, now: Date = new Date()): boolean {
  if (!endsAt) return false
  const t = Date.parse(endsAt)
  if (Number.isNaN(t)) return false
  return t + PAST_SETTLE_GRACE_MS < now.getTime()
}

/** Race-day anchor for assign-flow band checks (closes the P3 flagged in
 *  MY-2026-09-01-154049): the event's REAL `starts_at` when the my-api orders
 *  wire carries it, else the caller's fallback (default: now — the old honest
 *  approximation, kept for an older my-api that omits the field). */
export function raceDayFrom(startsAt: string | null | undefined, fallback: () => Date = () => new Date()): Date {
  if (startsAt) {
    const d = new Date(startsAt)
    if (!Number.isNaN(d.getTime())) return d
  }
  return fallback()
}

/** Invoice slot (BOARDROOM-2026-09-01-175306 item 4 — DARK): the single
 *  resolver behind every "View invoice" link. It resolves ONLY from a real
 *  box-office invoice artefact already on the order; the live wire carries
 *  none today (the invoice endpoint shape is still pending from tickets), so
 *  this returns `undefined` in production and the link renders NOWHERE — no
 *  dead links, ever. When tickets ships the invoice endpoint, wiring it HERE
 *  lights the slot everywhere at once. */
export function invoiceUrlForOrder(order: Order): string | undefined {
  return order.invoiceUrl || undefined
}

/** Browse-events link for the no-current-orders empty state (item 3). No
 *  established marketing/browse URL pattern exists in this repo yet, so:
 *  VITE_BROWSE_EVENTS_URL when explicitly configured, else the box-office
 *  ROOT derived from VITE_TICKETS_API_BASE — clearly a fallback, marked here
 *  per the commission ("a plain link to the box-office root is fine, mark it
 *  clearly"). Neither configured ⇒ null and the link is honestly absent. */
export function browseEventsUrl(env: Record<string, string | undefined> = import.meta.env): string | null {
  if (env.VITE_BROWSE_EVENTS_URL) return env.VITE_BROWSE_EVENTS_URL
  const api = env.VITE_TICKETS_API_BASE
  if (!api) return null
  try {
    return new URL(api).origin
  } catch {
    return null
  }
}
